import { Link } from "react-router-dom";
import { Building2, CheckCircle2, MapPin, Mail } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { usePublicConfig } from "@/lib/usePublicConfig";
import Seo from "@/components/Seo";

const formatEuro = (cents) => ((cents || 0) / 100).toFixed(2).replace(".", ",");

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-3 text-oc-textPrimary leading-relaxed">
      <CheckCircle2 className="w-5 h-5 text-oc-fair shrink-0 mt-0.5" />
      <span>{children}</span>
    </li>
  );
}

export default function VoorBedrijven() {
  const config = usePublicConfig();
  const price = config?.b2b_monthly_price_cents ?? 2900;

  return (
    <PublicLayout>
      <Seo title="Word OfferteExpert partner — €29/maand" description="Sluit je bedrijf aan op OfferteExpert en bereik particulieren die offertes laten checken in jouw branche en regio." />

      <section className="oc-section bg-oc-bg">
        <div className="oc-container max-w-4xl">
          <div className="flex items-center gap-2 text-oc-secondary uppercase tracking-wide font-semibold text-[12px] mb-3">
            <Building2 className="w-4 h-4" /> Voor bedrijven
          </div>
          <h1 className="font-display font-bold text-[44px] sm:text-[56px] text-oc-primary leading-[1.05]">
            Bereik particulieren <span className="text-oc-secondary">net voordat</span> ze kiezen
          </h1>
          <p className="mt-5 text-oc-textSecondary text-[18px] leading-relaxed max-w-3xl">
            Klanten die hun offerte laten analyseren bij OfferteExpert kunnen vrijwillig aanbevelingen ontvangen van bedrijven die wij zelf gecontroleerd hebben. Sluit je aan en verschijn in <strong>hun e-mailrapport</strong> wanneer jouw branche + regio matcht — voor een vast bedrag van <strong>€ {formatEuro(price)} per maand</strong>.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-8">
            <div className="bg-white rounded-oc border border-oc-border p-7 shadow-oc-card">
              <p className="text-[12px] uppercase tracking-wide font-semibold text-oc-textSecondary">Wat je krijgt</p>
              <ul className="mt-5 space-y-3">
                <Bullet>Vermelding in tot 3 aanbevelingen per matchend rapport (branche + regio)</Bullet>
                <Bullet>Direct linkje naar je website of e-mail in de mail naar de klant</Bullet>
                <Bullet>Geen klik-kosten, geen lock-in, opzegbaar per maand</Bullet>
                <Bullet>Eerlijke matching: alleen tonen aan wie écht in jouw markt zit</Bullet>
                <Bullet>Manuele controle door ons team — vertrouwen first, geen wildgroei</Bullet>
              </ul>
            </div>
            <div className="bg-oc-primary text-white rounded-oc p-7 shadow-oc-card-hover">
              <p className="text-[12px] uppercase tracking-wide font-semibold text-white/60">Tarief</p>
              <p className="font-mono font-bold text-[52px] mt-3 leading-none">€ {formatEuro(price)}</p>
              <p className="text-white/80 text-[14px] mt-1">/ maand · BTW excl. · betaling via MultiSafepay</p>
              <Link to="/voor-bedrijven/aanmelden">
                <Button
                  data-testid="b2b-cta"
                  className="mt-7 w-full bg-white text-oc-primary hover:bg-white/90 rounded-oc-btn h-14 text-[15px] font-semibold"
                >
                  Schrijf je bedrijf in
                </Button>
              </Link>
              <p className="text-white/60 text-[12px] mt-4 flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> Vragen? <a href="mailto:info@offerteexpert.be" className="underline ml-1">info@offerteexpert.be</a>
              </p>
            </div>
          </div>

          <div className="mt-14">
            <h2 className="font-display font-bold text-[28px] text-oc-primary">Hoe werkt het?</h2>
            <div className="mt-6 grid md:grid-cols-3 gap-5">
              {[
                { n: "01", t: "Aanmelden", d: "Vul je BTW, branche(s) en regio('s) in en betaal €29 voor de eerste maand." },
                { n: "02", t: "Controle", d: "Ons team verifieert je bedrijfsgegevens en reviews binnen 1 werkdag." },
                { n: "03", t: "Live", d: "Vanaf goedkeuring kom je automatisch in de aanbevelingsmails terecht." },
              ].map((s) => (
                <div key={s.n} className="bg-white rounded-oc border border-oc-border p-6">
                  <p className="font-mono text-oc-secondary font-bold text-[16px]">{s.n}</p>
                  <p className="font-display font-semibold text-oc-primary text-[18px] mt-1">{s.t}</p>
                  <p className="text-oc-textSecondary text-[14px] mt-2 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-14 bg-oc-subtle border border-oc-border rounded-oc p-7">
            <h3 className="font-display font-semibold text-oc-primary text-[20px] flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Voor welke regio's en branches kan ik aanmelden?
            </h3>
            <p className="text-oc-textSecondary text-[14px] mt-2 leading-relaxed">
              We accepteren alle provincies van België en Nederland. Branches: Renovatie & Bouw, Schilderwerk, Sanitair, Elektriciteit, Tuin & Buiten, Verwarming & Energie, Autoreparatie, Verhuis, Schoonmaak. Andere branches op aanvraag via mail.
            </p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
